<template>
  <v-container v-if="isLoading" fluid fill-height class="ma-0 pa-2">
    <v-row class="ma-0 pa-0">
      <v-col cols="12" class="ma-0 pa-0"> <loading-spinner /> </v-col>
    </v-row>
  </v-container>

  <v-container v-else fluid fill-width class="ma-0 pa-2">
    <v-app-bar app class="primary">
      <v-icon color="white" large>group_add</v-icon>
      <v-toolbar-title class="white--text pl-3"
        >Study Group Selector

        <div class="subtitle-1">
          Dataset:
          <v-tooltip bottom color="primary">
            <template v-slot:activator="{ on: tooltip }">
              <span v-on="{ ...tooltip }">{{ collection.label }}</span>
            </template>
            <span class="subtitle-1">{{
              getCollectionDescription(collection)
            }}</span>
          </v-tooltip>

          <span class="ml-3">
            Selected Study Group:
            <v-tooltip bottom color="primary">
              <template v-slot:activator="{ on: tooltip }">
                <span v-on="{ ...tooltip }">{{
                  cohort == null || cohort.id == -1 || !('label' in cohort)
                    ? 'None'
                    : cohort.label.replace(/^\[O\]_/, '')
                }}</span>
              </template>
              <span class="subtitle-1">{{
                cohort == null || cohort.id == -1 ? 'None' : cohort.query_string
              }}</span>
            </v-tooltip>
          </span>
        </div>
      </v-toolbar-title>

      <v-spacer></v-spacer>

      <v-chip
        v-if="useAutomatedAnalysisMode"
        color="yellow"
        text-color="black"
        class="mr-1"
        :disabled="!useAutomatedAnalysisMode"
        >Auto Mode ON</v-chip
      >

      <v-tooltip bottom color="primary">
        <template v-slot:activator="{ on: tooltip }">
          <v-toolbar-items v-on="{ ...tooltip }">
            <v-checkbox
              v-model="helpModeCheckbox"
              dark
              class="mt-5"
            ></v-checkbox>
            <span class="white--text font-weight-bold mt-5">Help Mode</span>
          </v-toolbar-items>
        </template>
        <span class="subtitle-1">
          {{ helpMode ? 'Disable' : 'Enable' }} help mode.
        </span>
      </v-tooltip>

      <v-chip
        v-if="showNextHelpChip"
        label
        close
        color="#4caf50"
        class="font-weight-bold white--text pa-2 my-1 mr-2 ml-4"
        @click:close="showNextHelpChip = false"
        >Click DATA ANALYTICS to proceed to the analysis step.</v-chip
      >

      <analysis-summary-btn-dialog
        ref="asum_btn"
        :collection-id="collectionId"
        :wait-for-confirmation="false"
        class="ml-3"
      ></analysis-summary-btn-dialog>
    </v-app-bar>

    <v-container
      v-if="!collection.has_visits_set"
      fluid
      fill-height
      class="ma-0 pa-0"
    >
      <v-row class="ma-0 pa-0">
        <v-col cols="12" class="ma-0 pa-0">
          <v-sheet color="white" height="100%" class="rounded-lg shadow">
            <span
              >ERROR - First/Last Visits must be set in order to use the Study
              Group Selector:<br clear="both"
            /></span>
            <v-tooltip top color="primary">
              <template v-slot:activator="{ on }">
                <v-btn
                  width="15em"
                  @click="
                    $router.push(`data_summary?collection=${collection.id}`)
                  "
                  v-on="on"
                >
                  Set Visits
                </v-btn>
              </template>
              <span class="subtitle-1"
                >View Data Summary and choose First &amp; Last Visit</span
              >
            </v-tooltip>
          </v-sheet>
        </v-col>
      </v-row>
    </v-container>

    <div v-else fill-width class="ma-0 pa-0">
      <analysis-tracker
        step="2"
        class="pb-2"
        :substep.sync="substep"
        :collection-id="collectionId"
        @createSimilar="createSimilar"
        @createNew="createNew"
        @nextStep="nextStep"
      ></analysis-tracker>

      <v-container
        fluid
        class="pa-0 mb-2"
        style="
        background-color: white; 
        width: 100%;"
      >
        <v-row class="ma-0 pa-0 py-1">
          <v-subheader class="subheading primary--text text--lighten-1 text-h6">
            CHOOSE ANALYSIS TYPE:
          </v-subheader>

          <v-btn-toggle
            v-model="selectedMode"
            mandatory
            color="primary"
            background-color="transparent"
            class="ml-4 mb-2 pa-0"
          >
            <v-tooltip bottom color="primary">
              <template v-slot:activator="{ on }">
                <v-btn
                  value="Predictor based"
                  :color="
                    selectedAnalysisMode == 'Predictor based'
                      ? 'primary'
                      : 'white'
                  "
                  style="border-color: gray !important;"
                  v-on="on"
                >
                  <span
                    :style="
                      'color: ' +
                        (selectedAnalysisMode == 'Predictor based'
                          ? 'white'
                          : 'black') +
                        ' !important;'
                    "
                    >Predictor based</span
                  >
                </v-btn>
              </template>
              <span class="subtitle-1 text-wrap"
                >What's the difference between the outcomes of different subject
                groups?</span
              >
            </v-tooltip>

            <v-tooltip bottom color="primary">
              <template v-slot:activator="{ on }">
                <v-btn
                  value="Outcome based"
                  :color="
                    selectedAnalysisMode == 'Predictor based'
                      ? 'white'
                      : 'primary'
                  "
                  style="border-color: gray !important;"
                  v-on="on"
                >
                  <span
                    :style="
                      'color: ' +
                        (selectedAnalysisMode == 'Predictor based'
                          ? 'black'
                          : 'white') +
                        ' !important;'
                    "
                    >Outcome based</span
                  >
                </v-btn>
              </template>
              <span class="subtitle-1 text-wrap"
                >What baseline features can explain differences in a selected
                outcome?</span
              >
            </v-tooltip>
          </v-btn-toggle>
        </v-row>

        <v-row align="center" class="ma-0 ml-4">
          <v-chip
            v-if="showModeHelpChip"
            label
            close
            color="#4caf50"
            class="font-weight-bold white--text pa-2 my-2"
            @click:close="showModeHelpChip = false"
          >
            <span
              v-if="selectedAnalysisMode == 'Outcome based'"
              class="subtitle-1"
              >What baseline features can explain differences in a selected
              outcome?</span
            >
            <span v-else class="subtitle-1"
              >What's the difference between the outcomes of different subject
              groups?</span
            >
          </v-chip>
        </v-row>

        <!-- <v-row align="center" class="ma-0 ml-4">
          <span class="subtitle-1" v-if="selectedAnalysisMode == 'Outcome based'">What's the difference between the subjects who do better and the subjects who do worse for a single chosen outcome variable?</span>
          <span class="subtitle-1" v-else>What's the difference between the outcomes of different subject groups?</span>
        </v-row> -->
      </v-container>

      <v-card
        v-if="selectedAnalysisMode === 'Outcome based'"
        :class="allPanelsClass"
      >
        <v-container fluid fill-width class="pa-0 ma-0">
          <v-row class="ma-0 pa-0">
            <v-col cols="12" class="ma-0 pa-0">
              <splitpanes class="default-theme">
                <pane size="65" :style="outputVarsStyle">
                  <v-sheet
                    color="white"
                    height="100%"
                    class="rounded-lg shadow pa-0 ma-0 mr-1"
                  >
                    <output-variables
                      ref="output_vars"
                      :expanded.sync="outExpanded"
                      :highlighted="outHighlighted"
                      :class="outputVarsClass"
                      :show-add-help="helpMode && substep == 2.1"
                      :show-filter-help="
                        helpMode && substep == '2.3' && !hasFilters
                      "
                      @userSelectedOutputVariables="userSelectedVariables"
                      @userChangedOutputVariable="userChangedVariable"
                      @comparePredefinedRanges="comparePredefinedRanges"
                      @savePredefinedRanges="savePredefinedRanges"
                    >
                    </output-variables>
                  </v-sheet>
                </pane>

                <pane
                  min-size="15"
                  max-size="75"
                  size="35"
                  :style="manageCohortsStyle"
                  class="pb-1"
                >
                  <ManageCohortsTable
                    class="ml-1"
                    :cohorts="collection_cohorts"
                    :collection-id="collectionId"
                    :show-new-help="helpMode && substep == '2.2'"
                    :show-save-help="helpMode && substep == '2.3' && hasFilters"
                    :filtered-data="filteredData"
                    :unfiltered-data="unfilteredData"
                    @selectedCohort="setSelectedCohortViaUrl"
                    @savedCohort="cohortSaved"
                    @newCohort="newCohort"
                  />
                </pane>
              </splitpanes>
            </v-col>
          </v-row>
        </v-container>

        <v-container fluid fill-width class="pa-0 ma-0">
          <v-row class="ma-0 pa-0">
            <v-col cols="12" class="ma-0 pa-0">
              <splitpanes class="default-theme" @resize="splitPaneResized()">
                <pane size="65" :style="inputVarsStyle" class="pb-1">
                  <v-sheet
                    color="white"
                    height="100%"
                    class="rounded-lg shadow pa-0 ma-0 mr-1"
                  >
                    <input-variables
                      ref="input_vars"
                      :expanded.sync="inExpanded"
                      :highlighted="inHighlighted"
                      :class="inputVarsClass"
                      :show-add-help="helpMode && substep == '2.2'"
                      :show-review-help="
                        helpMode && substep == 2.3 && hasFilters
                      "
                      :show-analytics-help="
                        helpMode && substep == '2.3' && hasFilters
                      "
                      @userSelectedInputVariables="userSelectedVariables"
                      @userChangedInputVariable="userChangedVariable"
                      @comparePredefinedRanges="comparePredefinedRanges"
                      @savePredefinedRanges="savePredefinedRanges"
                    >
                    </input-variables>
                  </v-sheet>
                </pane>

                <pane
                  min-size="15"
                  max-size="75"
                  size="35"
                  :style="analyticsTableStyle"
                  class="pb-1"
                >
                  <analytics
                    color-scheme="brewer5"
                    :show-review-help="helpMode && substep == 2.3 && hasFilters"
                    :substep="substep"
                    :has-filters="hasFilters"
                  />
                </pane>
              </splitpanes>
            </v-col>
          </v-row>

          <v-overlay
            absolute
            :value="helpMode && (substep != 2.3 || !hasFilters)"
          >
          </v-overlay>
        </v-container>
      </v-card>

      <v-card v-else :class="allPanelsClass">
        <v-container fluid fill-width class="pa-0 ma-0">
          <v-row class="ma-0 pa-0">
            <v-col cols="12" class="ma-0 pa-0">
              <splitpanes class="default-theme">
                <pane size="65" :style="inputVarsStyle" class="pb-1">
                  <v-sheet
                    color="white"
                    height="100%"
                    class="rounded-lg shadow pa-0 ma-0 mr-1"
                  >
                    <input-variables
                      ref="input_vars"
                      :expanded.sync="inExpanded"
                      :highlighted="inHighlighted"
                      :class="inputVarsClass"
                      :show-add-help="helpMode && substep == '2.1'"
                      :show-filter-help="
                        helpMode && substep == '2.3' && !hasFilters
                      "
                      :show-analytics-help="
                        helpMode && substep == '2.3' && hasFilters
                      "
                      @userSelectedInputVariables="userSelectedVariables"
                      @userChangedInputVariable="userChangedVariable"
                      @comparePredefinedRanges="comparePredefinedRanges"
                      @savePredefinedRanges="savePredefinedRanges"
                    >
                    </input-variables>
                  </v-sheet>
                </pane>

                <pane
                  min-size="15"
                  max-size="75"
                  size="35"
                  :style="manageCohortsStyle"
                  class="pb-1"
                >
                  <manage-cohorts-table
                    class="ml-1"
                    :cohorts="collection_cohorts"
                    :collection-id="collectionId"
                    :show-save-help="helpMode && substep == '2.3' && hasFilters"
                    @selectedCohort="setSelectedCohortViaUrl"
                    @savedCohort="cohortSaved"
                    @newCohort="newCohort"
                  />
                </pane>
              </splitpanes>
            </v-col>
          </v-row>
        </v-container>

        <v-container fluid fill-width class="ma-0 pa-0 pt-1">
          <v-row class="ma-0 pa-0">
            <v-col cols="12" class="ma-0 pa-0">
              <splitpanes class="default-theme">
                <pane size="65" :style="outputVarsStyle">
                  <output-variables
                    ref="output_vars"
                    :expanded.sync="outExpanded"
                    :highlighted="outHighlighted"
                    :disabled="true"
                    :class="outputVarsClass"
                    :show-add-help="helpMode && substep == 2.2"
                    :show-review-help="helpMode && substep == 2.3 && hasFilters"
                    @userSelectedOutputVariables="userSelectedVariables"
                    @userChangedOutputVariable="userChangedVariable"
                  />
                </pane>

                <pane
                  min-size="15"
                  max-size="75"
                  size="35"
                  :style="analyticsTableStyle"
                >
                  <analytics
                    color-scheme="brewer5"
                    :show-review-help="helpMode && substep == 2.3 && hasFilters"
                    :substep="substep"
                    :has-filters="hasFilters"
                  />
                </pane>
              </splitpanes>
            </v-col>
          </v-row>

          <v-overlay
            absolute
            :value="helpMode && (substep != 2.3 || !hasFilters)"
          >
          </v-overlay>
        </v-container>
      </v-card>
    </div>
    <analytics-popup
      ref="apop"
      color-scheme="brewer5"
      :name="analyticsPopupName"
      :cohorts="analyticsPopupCohorts"
      :select-range-fn="analyticsPopupSelectRangeFn"
      :cohort-prefix="analyticsPopupCohortPrefix"
    />
    <v-dialog v-model="aaDialog" persistent scrollable max-width="40%">
      <v-card class="rounded-lg" style="border: 3px solid #3f51b5;">
        <v-card-title color="white" class="ma-0 pa-2" primary-title>
          <span class="primary--text text--darken-3 title"
            >Automated Analysis Mode: Create Study Groups</span
          >
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="py-4">
          <v-list>
            <v-list-item
              v-for="(s, i) in aaSteps"
              :key="`prog-${i}`"
              :disabled="i > aaProgress"
            >
              <v-list-item-content>
                {{ s.title }}
                <v-list
                  v-if="
                    s.title == 'Creating study groups' && aaCohorts.length > 0
                  "
                >
                  <v-list-item v-for="(c, ci) in aaCohorts" :key="`aac-${ci}`">
                    <v-list-item-content> {{ c }} </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-list-item-content>
              <v-list-item-avatar>
                <v-icon v-if="i < aaProgress">done</v-icon>
              </v-list-item-avatar>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-card-actions v-if="aaDialogOKButton">
          <v-spacer></v-spacer>
          <v-btn
            :disabled="aaProgress < 4"
            class="primary white--text ma-0 px-2 mx-2"
            @click="aaToDataAnalytics()"
          >
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar
      v-model="helpModeNotify"
      color="#2a96f3"
      bottom
      multi-line
      class="pb-5"
    >
      <v-icon class="mr-2">help_outline</v-icon>
      <span class="font-weight-bold white--text subtitle-1">
        Toggle the help mode at any time by using the checkbox at the top right.
      </span>
    </v-snackbar>
  </v-container>
</template>

<script>
import { Splitpanes, Pane } from 'splitpanes';
import 'splitpanes/dist/splitpanes.css';
import { mapActions, mapState, mapGetters } from 'vuex';
import { actions, state, getters } from '@/store/modules/cohortManager/types';
import {
  getCollectionDescription,
  getLongScaleNameDefault,
} from '@/utils/helpers';
import logEvent from '@/utils/logging';
import AnalysisTracker from '@/components/common/AnalysisTracker.vue';
import ManageCohortsTable from '@/components/CohortManager/ManageCohortsTable.vue';
import Analytics from '@/components/CohortManager/Analytics.vue';
import AnalyticsPopup from '@/components/CohortManager/AnalyticsPopup.vue';
import InputVariables from '@/components/CohortManager/InputVariables.vue';
import OutputVariables from '@/components/CohortManager/OutputVariables.vue';
import AnalysisSummaryBtnDialog from '@/components/DataExplorer/AnalysisSummaryBtnDialog.vue';
import { actions as collaborationActions } from '@/store/modules/collaboration/types';

var aaSteps = [
  { title: 'Loading data' },
  { title: 'Adding predictor variables' },
  { title: 'Adding outcome variables' },
  { title: 'Creating study groups' },
  { title: 'Done' },
];

export default {
  name: 'StudyGroupManager',
  components: {
    AnalysisTracker,
    InputVariables,
    OutputVariables,
    ManageCohortsTable,
    Analytics,
    AnalyticsPopup,
    Splitpanes,
    Pane,
    AnalysisSummaryBtnDialog,
  },
  props: {
    collectionId: {
      type: Number,
      required: true,
    },
    aaPredictors: {
      type: Array,
      default: () => [],
    },
    aaOutputs: {
      type: Array,
      default: () => [],
    },
    aaRanges: {
      type: String,
      default: '',
    },
    aaMCS: {
      type: String,
      default: '',
    },
    aaWhichOutcomes: {
      type: String,
      default: '',
    },
    cohortId: {
      type: Number,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      isLoadingCohort: false,
      substep: '2.1',
      inExpanded: true,
      outExpanded: false,
      inHighlighted: true,
      outHighlighted: false,
      getCollectionDescription: getCollectionDescription,
      analyticsPopupName: 'Tertiles',
      analyticsPopupCohortPrefix: '',
      analyticsPopupCohorts: [],
      analyticsPopupSelectRangeFn: x => x,
      helpModeCheckbox: false,
      helpModeNotify: false,
      showModeHelpChip: false,
      showNextHelpChip: false,
      selectedMode: 'Predictor based',
      aaDialog: false,
      aaDialogOKButton: false,
      aaProgress: 0,
      aaLastUpdate: null,
      aaMinStepTime: 1,
      aaSteps: aaSteps,
      aaCohorts: [],
    };
  },
  computed: {
    ...mapGetters('cohortManager', {
      hasUserSelectedCohort: getters.HAS_USER_SELECTED_COHORT,
    }),
    ...mapState('cohortManager', {
      collection: state.COLLECTION,
      cohort: state.COHORT,
      cohorts: state.COHORTS,
      inputVariables: state.INPUT_VARIABLES,
      outputVariables: state.OUTPUT_VARIABLES,
      useLongScaleNames: state.USE_LONG_SCALE_NAMES,
      filteredData: state.FILTERED_DATA,
      unfilteredData: state.UNFILTERED_DATA,
      helpMode: state.HELP_MODE,
      selectedAnalysisMode: state.SELECTED_ANALYSIS_MODE,
      cvMetadata: state.CV_METADATA,
    }),
    // cohorts are collection-specific
    collection_cohorts() {
      const cch = [];
      const cid = this.collection.id;

      this.cohorts.forEach(e => {
        if (e.collection_id === cid) {
          if (
            this.selectedAnalysisMode == 'Outcome based' &&
            e.label.startsWith('[O]_')
          ) {
            cch.push(e);
          } else if (
            this.selectedAnalysisMode == 'Predictor based' &&
            !e.label.startsWith('[O]_')
          ) {
            cch.push(e);
          }
        }
      });
      // sort most recent first
      // using id because resolution of date_generated isn't sufficiently high res
      var scc = [...cch].sort((x, y) => y['id'] - x['id']);
      return scc;
    },
    allPanelsClass() {
      var cl = 'ma-0 pa-0';
      if (this.helpMode && this.substep == '2.3' && this.hasFilters) {
        cl = cl + ' help_mode';
      }
      return cl;
    },
    inputVarsClass() {
      var cl = 'ma-0 pt-0';
      if (this.selectedAnalysisMode == 'Predictor based') {
        if (
          this.helpMode &&
          (this.substep == '2.1' || (this.substep == '2.3' && !this.hasFilters))
        ) {
          cl = cl + ' help_mode';
        }
      } else {
        if (this.helpMode && this.substep == '2.2') {
          cl = cl + ' help_mode';
        }
      }
      return cl;
    },
    outputVarsClass() {
      var cl = 'ma-0 pt-0 mr-1';
      if (this.selectedAnalysisMode == 'Predictor based') {
        if (this.helpMode && this.substep == '2.2') {
          cl = cl + ' help_mode';
        }
      } else {
        if (
          this.helpMode &&
          (this.substep == '2.1' || (this.substep == '2.3' && !this.hasFilters))
        ) {
          cl = cl + ' help_mode';
        }
      }
      return cl;
    },
    inputVarsStyle() {
      if (this.selectedAnalysisMode == 'Predictor based') {
        if (this.helpMode && this.substep != '2.2') {
          return 'z-index: 100;';
        }
      } else {
        if (
          this.helpMode &&
          (this.substep == '2.2' || (this.substep == '2.3' && this.hasFilters))
        ) {
          return 'z-index: 100;';
        }
      }
      return 'z-index: auto';
    },
    manageCohortsStyle() {
      // always show Manage Cohorts in Help Mode:
      if (this.helpMode) {
        return 'z-index: 100;';
      }
      return 'z-index: auto';
    },
    outputVarsStyle() {
      if (this.selectedAnalysisMode == 'Predictor based') {
        if (
          this.helpMode &&
          (this.substep == '2.2' || (this.substep == '2.3' && this.hasFilters))
        ) {
          return 'z-index: 100;';
        }
      } else {
        if (this.helpMode && this.substep != '2.2') {
          return 'z-index: 100;';
        }
      }
      return 'z-index: auto';
    },
    analyticsTableStyle() {
      if (this.helpMode && this.substep == '2.3' && this.hasFilters) {
        return 'z-index: 100;';
      }
      return 'z-index: auto';
    },
    showNextHelp() {
      return this.helpMode && this.collection_cohorts.length >= 2;
    },
    hasFilters() {
      return this.filteredData.length < this.unfilteredData.length;
    },
    useAutomatedAnalysisMode() {
      return this.aaPredictors.length + this.aaOutputs.length > 0;
    },
  },
  watch: {
    'collection.id': {
      handler(id) {
        if (id) {
          this.$store.dispatch(
            'collaboration/' + collaborationActions.SET_ACTIVE_COLLECTION,
            this.collection
          );
        }
      },
      immediate: true,
    },
    cohortId(id) {
      if (id) this.selectCohortById(id);
    },
    substep() {
      if (this.substep === '2.1') {
        this.inHighlighted = true;
        this.outHighlighted = false;
      } else if (this.substep === '2.2') {
        this.inHighlighted = false;
        this.outHighlighted = true;
      } else if (this.substep === '2.3') {
        if (this.hasFilters) {
          this.inHighlighted = false;
          this.outHighlighted = false;
        } else {
          this.inHighlighted = true;
          this.outHighlighted = false;
        }
      }
    },
    inputVariables() {
      this.updateSubstep();
    },
    outputVariables() {
      this.updateSubstep();
    },
    filteredData() {
      this.updateSubstep();
    },
    unfilteredData() {
      this.updateSubstep();
    },
    helpModeCheckbox(helpOn) {
      if (helpOn != this.helpMode) this.setHelpMode(helpOn);
    },
    helpMode(helpOn) {
      this.helpModeNotify = helpOn;
      this.showModeHelpChip = helpOn;
      logEvent(
        this.$gtag,
        null,
        null,
        'helpmode_' + (helpOn ? 'on' : 'off'),
        'help',
        this.substep
      );
    },
    showNextHelp(show) {
      this.showNextHelpChip = show;
    },
    collection(c) {
      let hasICD10Codes = false;
      if (c != null && 'subject_variables_list' in c) {
        c.subject_variables_list.forEach(sv => {
          if (sv.ontology.label.startsWith('ICD10_Code')) hasICD10Codes = true;
        });
      }
      if (hasICD10Codes) {
        this.fetchCvMetadata();
      }
    },
    selectedMode(sm) {
      if (sm != this.selectedAnalysisMode) {
        this.setAnalysisMode(sm);
        this.$nextTick(() => {
          // clear filters and clear variables
          const nc = { id: -1 };
          this.setCohort(nc);
          this.setSelectedCohortViaUrl(nc);
        });
      }
    },
    selectedAnalysisMode(sm) {
      if (sm != this.selectedMode) {
        this.selectedMode = sm;
      }
    },
  },
  async mounted() {
    // reset help mode to false
    this.setHelpMode(false);
    // reset analysis mode
    if (this.cohortId == null) {
      this.setAnalysisMode('Predictor based');
    } else if (this.selectedMode != this.selectedAnalysisMode) {
      this.selectedMode = this.selectedAnalysisMode;
    }
  },
  async created() {
    if (this.useAutomatedAnalysisMode) {
      this.aaDialog = true;
      this.aaProgress = 0;
    }
    this.resetAllStoreData();
    this.isLoading = true;
    await this.fetchCollection(this.collectionId);
    await this.fetchData();
    await this.fetchCohorts(this.collectionId);
    if (this.cohortId == null) this.newCohort();
    var datasets = this.collection.studies.map(d => d.study);
    var useLongScaleNames = getLongScaleNameDefault(datasets);
    this.setUseLongScaleNames(useLongScaleNames);
    this.isLoading = false;
    if (this.useAutomatedAnalysisMode) {
      this.doAutomatedAnalysis();
    } else if (this.cohortId) {
      this.$nextTick(() => this.selectCohortById(this.cohortId));
    } else if (this.collection_cohorts.length > 0) {
      this.$nextTick(() =>
        this.setSelectedCohortViaUrl(
          this.collection_cohorts[this.collection_cohorts.length - 1]
        )
      );
    }
  },
  methods: {
    ...mapActions('cohortManager', {
      clearAllFilters: actions.CLEAR_ALL_FILTERS,
      fetchCollection: actions.FETCH_COLLECTION,
      fetchCohorts: actions.FETCH_COHORTS,
      fetchData: actions.FETCH_DATA,
      resetAllStoreData: actions.RESET_ALL_STORE_DATA,
      setCohort: actions.SET_COHORT,
      setCohortOutcomeBased: actions.SET_COHORT_OUTCOMEBASED,
      setCohortNoReset: actions.SET_COHORT_NO_RESET,
      setUseLongScaleNames: actions.SET_USE_LONG_SCALE_NAMES,
      setHelpMode: actions.SET_HELP_MODE,
      setInputVariables: actions.SET_INPUT_VARIABLES,
      setOutputVariables: actions.SET_OUTPUT_VARIABLES,
      saveCohort: actions.SAVE_COHORT,
      setAnalysisMode: actions.SET_ANALYSIS_MODE,
      fetchCvMetadata: actions.FETCH_CV_METADATA,
    }),
    splitPaneResized() {
      // force plotly to fit width
      window.dispatchEvent(new Event('resize'));
    },
    updateSubstep() {
      var n_iv = this.inputVariables.length;
      var n_ov = this.outputVariables.length;
      var new_ss = this.substep;

      if (n_iv > 0 && n_ov > 0) {
        new_ss = '2.3';
      } else if (n_iv > 0) {
        new_ss = '2.2';
      } else {
        new_ss = '2.1';
      }
      if (new_ss != this.substep) this.substep = new_ss;
    },
    userSelectedVariables() {
      this.setCohortNoReset({ id: -1 });
      this.setSelectedCohortViaUrl({ id: -1 });
    },
    userChangedVariable() {
      this.setCohortNoReset({ id: -1 });
      this.setSelectedCohortViaUrl({ id: -1 });
    },
    cohortSaved(success) {
      if (success) {
        // reset all filters
        this.clearAllFilters();
        // return to "add filters" step
        this.substep = '2.3';
      }
    },
    // create entirely new cohort resetting everything
    createNew(success) {
      if (success) {
        // return to "choose predictor variables" step
        this.setNewCohort({ id: -1 });
        this.setSelectedCohortViaUrl({ id: -1 });
        this.substep = '2.1';
      }
    },
    // create new cohort similar to the most-recently-created one
    createSimilar(success) {
      if (success) {
        var ncc = this.collection_cohorts.length;
        // load last cohort in the list
        if (ncc > 0) {
          var last_cohort = this.collection_cohorts[ncc - 1];
          this.setNewCohort(last_cohort);
        }
        // return to "add filters" step
        this.substep = '2.3';
      }
    },
    setSelectedCohortViaUrl(cohort) {
      if (this.useAutomatedAnalysisMode) return;
      let newPath = '';
      if (cohort.id == -1) {
        newPath = `/cohorts?collection=${this.collection.id}`;
      } else {
        newPath = `/cohorts?collection=${this.collection.id}&cohortId=${
          cohort.id
        }`;
      }
      if (newPath != this.$route.fullPath) this.$router.push(newPath);
    },
    selectCohortById(id) {
      const cohort = this.cohorts.find(c => c.id === id);
      if (cohort) this.cohortSelected(cohort);
    },
    cohortSelected(newCohort) {
      if (newCohort == this.cohort) {
        return;
      }
      this.setNewCohort(newCohort);
      if (
        newCohort.id != -1 &&
        newCohort.label !== 'New Cohort' &&
        newCohort.label !== 'Choose Cohort'
      ) {
        this.substep = '2.3';
      } else {
        this.substep = '2.1';
      }
    },
    newCohort() {
      this.setCohortNoReset({ id: -1 });
      this.setSelectedCohortViaUrl({ id: -1 });
    },
    cohortDeleted() {
      this.substep = '2.1';
    },
    async setNewCohort(newCohort) {
      this.isLoadingCohort = true;
      // if known, type of cohort determines selectedAnalysisMode
      if ('label' in newCohort) {
        if (newCohort.label.startsWith('[O]_')) {
          if (this.selectedAnalysisMode != 'Outcome based') {
            this.setAnalysisMode('Outcome based');
          }
          this.$nextTick(() => {
            this.setCohortOutcomeBased(newCohort);
            this.isLoadingCohort = false;
          });
        } else {
          if (this.selectedAnalysisMode != 'Predictor based') {
            this.setAnalysisMode('Predictor based');
          }
          this.$nextTick(() => {
            this.setCohort(newCohort);
            this.isLoadingCohort = false;
          });
        }
      }
      // type of cohort not known, use current analysis mode
      else {
        if (this.selectedAnalysisMode == 'Outcome based') {
          this.$nextTick(() => {
            this.setCohortOutcomeBased(newCohort);
            this.isLoadingCohort = false;
          });
        } else {
          this.$nextTick(() => {
            this.setCohort(newCohort);
            this.isLoadingCohort = false;
          });
        }
      }
      this.isLoadingCohort = false;
    },
    async comparePredefinedRanges(ranges) {
      this.analyticsPopupCohorts = ranges.cohorts;
      this.analyticsPopupName = ranges.name;
      this.analyticsPopupCohortPrefix = this.useLongScaleNames
        ? ranges.variable.label
        : ranges.variable.abbreviation;
      // handle case where predictor variable is a single component (first/last/change/roc) of an observation variable
      if (
        ranges.variable.label.match(
          /first visit|last visit|change|rate of change/i
        )
      ) {
        this.analyticsPopupCohortPrefix += ' ' + ranges.variable.label;
      }
      this.analyticsPopupCohortPrefix += ' - ';
      this.analyticsPopupSelectRangeFn = ranges.select_fn;
      this.$refs.apop.show(true);
    },
    async savePredefinedRanges(ranges) {
      this.analyticsPopupCohorts = ranges.cohorts;
      this.analyticsPopupName = ranges.name;
      // handle case where predictor variable is a single component (first/last/change/roc) of an observation variable
      let isObservationVarComponent = ranges.variable.label.match(
        /first visit|last visit|change|rate of change/i
      );
      this.analyticsPopupCohortPrefix = this.useLongScaleNames
        ? isObservationVarComponent
          ? ranges.variable.parentLabel
          : ranges.variable.label
        : ranges.variable.abbreviation;
      // handle case where predictor variable is a single component (first/last/change/roc) of an observation variable
      if (isObservationVarComponent) {
        this.analyticsPopupCohortPrefix += ' ' + ranges.variable.label;
      }
      this.analyticsPopupCohortPrefix += ' - ';
      this.analyticsPopupSelectRangeFn = ranges.select_fn;
      this.$refs.apop.saveCohorts(
        ranges.cohorts,
        this.analyticsPopupCohortPrefix
      );
    },
    nextStep() {
      this.$refs.asum_btn.dialog = true;
    },
    // TODO - copied from DataSummary
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    async updateAutomatedAnalysisProgress(progress) {
      var ts = Date.now();
      if (this.aaLastUpdate == null) {
        this.aaProgress = progress;
      } else {
        var diff = (ts - this.aaLastUpdate) / 1000.0;
        if (diff < this.aaMinStepTime) {
          var remaining = this.aaMinStepTime - diff;
          await this.sleep(remaining * 1000);
        }
        this.aaProgress = progress;
      }
      this.aaLastUpdate = Date.now();
    },
    async doAutomatedAnalysis() {
      await this.updateAutomatedAnalysisProgress(0);

      var aa_ph = {};
      this.aaPredictors.map(vid => {
        aa_ph[vid] = true;
      });
      var aa_oh = {};
      this.aaOutputs.map(vid => {
        aa_oh[vid] = true;
      });
      // unpack aaWhichOutcomes
      const d1 = '|';
      const d2 = '||';
      const vmwo = {};
      if (this.aaWhichOutcomes != '') {
        this.aaWhichOutcomes.split(d2).map(r => {
          var [k, v] = r.split(d1);
          v.split(',').map(vid => {
            vmwo[vid] = k;
          });
        });
      }

      // ** add predictor variables
      await this.updateAutomatedAnalysisProgress(1);
      var ivd = this.$refs.input_vars.$refs.input_toolbar.$refs.input_dialog;
      // open input vars dialog
      ivd.openInputVariableDialog = true;
      await this.sleep(this.aaMinStepTime * 1000);

      // select input vars
      ivd.inputVariables.forEach(v => {
        let includeDataset =
          v.abbreviation == 'Dataset' && this.collection.studies.length > 1;
        if (v.id in aa_ph || includeDataset) {
          // longitudinal variable - need to select only first, last, change, or ROC
          if (v.id in vmwo) {
            let child_id = vmwo[v.id] + '-' + v.id;
            v.children.forEach(c => {
              if (c.id == child_id) {
                c.inSelected = true;
                ivd.cbChange(c);
              }
            });
          }
          // select all
          else {
            v.inmSelected = true;
            ivd.masterCbChange(v);
          }
        }
      });
      await this.sleep(this.aaMinStepTime * 2000);

      // close input vars dialog
      ivd.openInputVariableDialog = false;

      await this.updateAutomatedAnalysisProgress(2);
      await this.sleep(this.aaMinStepTime * 1000);

      // ** add outcome variables
      var ovd = this.$refs.output_vars.$refs.output_toolbar.$refs.output_dialog;

      // open output vars dialog
      ovd.openOutputVariableDialog = true;
      await this.sleep(this.aaMinStepTime * 1000);

      // select output vars
      ovd.outputVariables.forEach(v => {
        if (v.id in aa_oh) {
          v.outmSelected = true;
          ovd.masterCbChange(v);
        }
      });
      await this.sleep(this.aaMinStepTime * 2000);

      // close output vars dialog
      ovd.openOutputVariableDialog = false;

      await this.updateAutomatedAnalysisProgress(3);
      await this.sleep(this.aaMinStepTime * 1000);

      // unpack aaRanges, aaMCS
      const vranges = {};
      if (this.aaRanges != '') {
        this.aaRanges.split(d2).map(r => {
          var [k, v] = r.split(d1);
          v.split(',').map(vid => {
            vranges[vid] = k;
          });
        });
      }
      const vmcs = {};
      if (this.aaMCS != '') {
        this.aaMCS.split(d2).map(r => {
          var [k, v] = r.split(d1);
          v.split(',').map(vid => {
            vmcs[vid] = k;
          });
        });
      }

      // create cohorts for each predictor variable
      var ivc = this.$refs.input_vars.$refs.input_charts;
      var hist_charts = [];
      var col_charts = [];

      ivd.inputVariables.forEach(v => {
        // 'ivc-393' for predictor vs 'ivc-lastVisit-393' for outcome as predictor
        let vc_id = 'ivc-' + v.id;
        if (v.id in vmwo) {
          vc_id = 'ivc-' + vmwo[v.id] + '-' + v.id;
        }

        if (ivc.$refs[vc_id]) {
          if ('hist_chart' in ivc.$refs[vc_id][0].$refs) {
            var hc = ivc.$refs[vc_id][0].$refs.hist_chart;
            hc.tab = 'predef';
            if (v.id in vranges) {
              hc.predef_radio = vranges[v.id];
            } else {
              hc.predef_radio = 'quartiles';
            }
            hist_charts.push(hc);
          } else if ('col_chart' in ivc.$refs[vc_id][0].$refs) {
            var cc = ivc.$refs[vc_id][0].$refs.col_chart;
            var cch = { chart: cc, cohorts: [] };
            col_charts.push(cch);

            // minimum category size - all smaller categories will be merged into 'Other'
            var mcs = 1;
            if (v.id in vmcs) {
              mcs = vmcs[v.id];
            }

            // group data by category
            var cat2d = {};
            var cat2c = {};
            this.unfilteredData.map(d => {
              let dv = cc.dimension.accessor(d);
              if (!(dv in cat2d)) {
                cat2d[dv] = [];
                cat2c[dv] = 0;
              }
              cat2d[dv].push(d);
              cat2c[dv] += 1;
            });

            var s_args = {
              collection: this.collection,
              inputVariables: this.inputVariables,
              outputVariables: this.outputVariables,
              minCatSize: mcs,
            };

            // merge smaller categories
            var small_cats = [];
            Object.keys(cat2d).forEach(cat => {
              var ct = cat2c[cat];
              if (ct < mcs) {
                small_cats.push(cat);
              }
              // make single category cohort
              else {
                var queries = {};
                queries[cc.dimensionName] = [{ value: cat }];
                var args = {
                  name: cc.dimensionName + ' - ' + cat,
                  queries: queries,
                  subjectIds: cat2d[cat].map(d => d.subject_id),
                  ...s_args,
                };
                cch['cohorts'].push(args);
              }
            });

            // create multi-category 'Other' category if needed
            if (small_cats.length > 0) {
              var queries = {};
              queries[cc.dimensionName] = small_cats.map(c => {
                return { value: c };
              });
              var subjIds = [];
              small_cats.map(c => {
                cat2d[c].map(d => subjIds.push(d.subject_id));
              });
              var args = {
                name:
                  cc.dimensionName + ' - Other [' + small_cats.join(',') + ']',
                queries: queries,
                subjectIds: subjIds,
                ...s_args,
              };
              cch['cohorts'].push(args);
            }
          }
        }
      });

      // hist_charts
      for (var i = 0; i < hist_charts.length; ++i) {
        this.aaCohorts.push(
          hist_charts[i].dimensionName + ' - ' + hist_charts[i].predef_radio
        );
        await hist_charts[i].savePredefs(hist_charts[i].predef_radio);
        await this.sleep(this.aaMinStepTime * 1000);
      }

      // col_charts
      for (i = 0; i < col_charts.length; ++i) {
        var cc = col_charts[i];
        var nc = cc['cohorts'].length;
        this.aaCohorts.push(
          cc['chart'].dimensionName + ' - ' + nc + ' study groups'
        );
        for (var j = 0; j < nc; ++j) {
          await this.saveCohort(cc['cohorts'][j]);
          await this.sleep(this.aaMinStepTime * 1000);
        }
      }

      await this.updateAutomatedAnalysisProgress(4);
      await this.updateAutomatedAnalysisProgress(5);
      if (!this.aaDialogOKButton) {
        await this.sleep(2000);
        this.aaToDataAnalytics();
      }
    },
    // go to data analytics
    aaToDataAnalytics() {
      var query = { collection: this.collection.id };
      if (this.useAutomatedAnalysisMode) {
        query['aa_predictors'] = this.aaPredictors;
        query['aa_outputs'] = this.aaOutputs;
      }

      this.$router.push({ name: 'dataExplorer', query: query });
    },
  },
};
</script>

<style scoped>
/* Help mode */
.help_mode {
  border: 7px solid #fceb3b !important;
}
</style>
